import { useRef } from "react";
import styled from "styled-components/macro";
import { useQuery } from "@tanstack/react-query";
import {
  BoxHeader,
  BoxNavLeft,
  BoxTitle,
  BoxContent,
  BoxNavRight,
  BoxNavButton,
  BoxNavLink,
  Box,
} from "../Box";
import { Close, CalendarPlus, Icon } from "../Icons";
import Button from "../Button";
import Title from "../Title";
import SidePanel from "../SidePanel";
import EventEditor from "./Editor";
import Stack from "../Stack";
import { THEME_COLOR } from "../../vars";
import { Label } from "../Input";
import ContactsWell from "../ContactsWell";
import { useSkipioApi } from "../../api";
import useLoadAll from "../../hooks/use-load-all";
import { useToast } from "../../hooks/use-toast";
import useMoment from "../../hooks/use-moment";
import CampaignPreview from "../CampaignEditor/CampaignPreview";
import { useSkipioQuery } from "../../hooks/use-query";
import { fetchCampaign } from "../../api/campaigns";

export default function Viewer(props) {
  const moment = useMoment();
  const skipioApi = useSkipioApi();
  const inviteCodeRef = useRef(null);
  const { hide, eventId } = props;

  const event = useSkipioQuery({ queryKey: ["events", eventId] }).data;
  const campaign = useQuery({
    queryKey: ["campaigns", event.campaignId],
    queryFn: () => fetchCampaign(event.campaignId),
    suspense: false,
    useErrorBoundary: true,
  }).data;
  const { data: eventContacts, loading: loadingContacts } = useLoadAll(
    skipioApi.eventContacts,
    {
      eventId: event.id,
      status: "current",
    }
  );

  const showToast = useToast();

  return (
    <>
      <BoxHeader stacked>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent style={{ padding: 0 }}>
        <BoxContent shrink dark>
          <Title data-ignore="true">
            {event.name} <small>{moment(event.startDate).format("lll")}</small>
          </Title>
        </BoxContent>
        <BoxHeader>
          <BoxNavLeft>
            <BoxNavLink to="#" className="active">
              Details
            </BoxNavLink>
          </BoxNavLeft>
          <BoxNavRight>
            <SidePanel
              trigger={({ ref, show }) => (
                <Button variant="link" ref={ref} onClick={show}>
                  Edit Event
                </Button>
              )}
              content={({ hide: hideEditor }) => (
                <EventEditor
                  {...props}
                  eventId={event.id}
                  hide={hideEditor}
                  popToTop={() => {
                    hideEditor();
                    hide();
                  }}
                />
              )}
            />
          </BoxNavRight>
        </BoxHeader>
        <BoxContent shrink>
          <Stack>
            <Box>
              <BoxHeader>
                <BoxNavLeft>
                  <BoxTitle data-ignore="true">{event.name}</BoxTitle>
                </BoxNavLeft>
                <BoxNavRight>
                  <Button
                    variant="link"
                    onClick={() => {
                      inviteCodeRef.current.select();
                      document.execCommand("copy");
                      showToast("Invite link copied to clipboard", {
                        success: true,
                      });
                    }}
                  >
                    Copy Invite Link
                  </Button>
                  <input
                    style={{
                      position: "absolute",
                      top: -99999,
                      left: -99999,
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                    value={event.shortRsvpUrl}
                    readOnly
                    ref={inviteCodeRef}
                  />
                </BoxNavRight>
              </BoxHeader>
              <BoxContent>
                <Stack>
                  {Boolean(event.imageUrl) && <Img src={event.imageUrl} />}
                  <DateDescription>
                    <CalendarPlus />
                    <div>
                      <Title>
                        {moment(event.startDate).format("ddd, MMMM D, YYYY")}
                        <small>{`${moment(event.startDate).format(
                          "LT"
                        )} - ${moment(event.endDate).format("LT")}`}</small>
                      </Title>
                    </div>
                  </DateDescription>
                </Stack>
              </BoxContent>
            </Box>
            {(event.description || "").trim() && (
              <>
                <Label>Description</Label>
                <P data-ignore="true">{event.description}</P>
              </>
            )}
            {!loadingContacts && (
              <>
                <Label>Attendees</Label>
                <ContactsWell
                  placeholder="There are no contacts in this event"
                  value={{
                    contactIds: eventContacts.map((ec) => ec.contactId),
                    groupIds: [],
                  }}
                  canAdd={false}
                  canRemove={false}
                />
              </>
            )}
            {Boolean(campaign) && (
              <>
                <Label>Reminder Campaign</Label>
                <Box>
                  <BoxContent>
                    <CampaignPreview campaignId={campaign.id} />
                  </BoxContent>
                </Box>
              </>
            )}
          </Stack>
        </BoxContent>
      </BoxContent>
    </>
  );
}

const P = styled.p`
  color: #676767;
  line-height: 1.8em;
`;

const DateDescription = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${Icon} {
    margin-right: 16px;
    color: ${THEME_COLOR};
  }
`;

const Img = styled.img`
  border-radius: 7px;
  width: 100%;
`;
